import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const shimmerGradient = 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)';

// Base Skeleton styles
const SkeletonBase = styled.div<{ $width?: string; $height?: string; $animation?: boolean }>`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '20px'};
  background: ${props => props.$animation
    ? `${shimmerGradient}`
    : '#e0e0e0'};
  background-size: ${props => props.$animation ? '200% 100%' : 'auto'};
  border-radius: 6px;
  animation: ${props => props.$animation ? `${shimmerAnimation} 1.5s ease-in-out infinite` : 'none'};

  @keyframes ${shimmerAnimation} {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Skeleton Card - for assignment cards, student cards, etc.
export const SkeletonCard = styled(SkeletonBase)`
  min-height: ${props => props.$height || '120px'};
  border-radius: 12px;
`;

// Skeleton Circle - for avatars, icons, etc.
export const SkeletonCircle = styled(SkeletonBase)<{ $size?: string }>`
  width: ${props => props.$size || '40px'};
  height: ${props => props.$size || '40px'};
  border-radius: 50%;
`;

// Skeleton Text - for text placeholders with different widths
export const SkeletonText = styled(SkeletonBase)<{ $width?: string }>`
  height: 16px;
  margin-bottom: 8px;

  &.title {
    height: 24px;
    margin-bottom: 12px;
    width: ${props => props.$width || '60%'};
  }

  &.subtitle {
    height: 18px;
    margin-bottom: 10px;
    width: ${props => props.$width || '40%'};
  }

  &.paragraph {
    height: 14px;
    margin-bottom: 6px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

// Skeleton Button - for button placeholders
export const SkeletonButton = styled(SkeletonBase)`
  height: 40px;
  width: ${props => props.$width || '120px'};
  border-radius: 8px;
`;

// Skeleton Avatar - for user avatars
export const SkeletonAvatar = styled(SkeletonBase)<{ $size?: string }>`
  width: ${props => props.$size || '48px'};
  height: ${props => props.$size || '48px'};
  border-radius: 50%;
`;

// Assignment Card Skeleton
interface AssignmentCardSkeletonProps {
  count?: number;
}

export const AssignmentCardSkeleton: React.FC<AssignmentCardSkeletonProps> = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonWrapper key={index}>
          <SkeletonHeader>
            <SkeletonCircle $size="20px" />
            <SkeletonText $width="200px" className="title" />
          </SkeletonHeader>
          <SkeletonBody>
            <SkeletonText $width="100px" />
            <SkeletonText $width="150px" className="paragraph" />
            <SkeletonText $width="120px" className="paragraph" />
          </SkeletonBody>
          <SkeletonProgress>
            <SkeletonText $width="60px" />
            <SkeletonProgressBar $height="8px" />
          </SkeletonProgress>
        </SkeletonWrapper>
      ))}
    </>
  );
};

const SkeletonWrapper = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const SkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const SkeletonBody = styled.div`
  margin-bottom: 16px;
`;

const SkeletonProgress = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid rgba(0, 0, 0, 0.05);
`;

const SkeletonProgressBar = styled(SkeletonBase)`
  flex: 1;
  $height: 8px;
  border-radius: 4px;
  margin-top: 8px;
`;

// Student Card Skeleton
export const StudentCardSkeleton: React.FC = () => {
  return (
    <StudentSkeletonWrapper>
      <StudentSkeletonHeader>
        <SkeletonAvatar $size="40px" />
        <StudentSkeletonInfo>
          <SkeletonText $width="120px" className="title" />
          <SkeletonText $width="80px" className="subtitle" />
        </StudentSkeletonInfo>
      </StudentSkeletonHeader>
    </StudentSkeletonWrapper>
  );
};

const StudentSkeletonWrapper = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`;

const StudentSkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StudentSkeletonInfo = styled.div`
  flex: 1;
`;

// Page Loading Skeleton
export const PageLoadingSkeleton: React.FC = () => {
  return (
    <PageLoadingWrapper>
      <LoadingSpinner />
      <LoadingText>Yüklənir...</LoadingText>
    </PageLoadingWrapper>
  );
};

const PageLoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 24px;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 5px solid #e0e7ff;
  border-top: 5px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
`;

// Grid of Skeleton Cards
export const SkeletonGrid: React.FC<{ count?: number; cols?: number }> = ({ count = 3, cols = 3 }) => {
  return (
    <SkeletonGridWrapper $cols={cols}>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={index} $height="180px" $animation />
      ))}
    </SkeletonGridWrapper>
  );
};

const SkeletonGridWrapper = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 3}, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(${props => Math.min(props.$cols || 3, 2)}, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export default SkeletonBase;
